import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { ArticleVO } from '@article/model/vo/article.vo';
import { CaeProjectImportUtils } from '../../cae/util/cae-project-import-utils';
import { ArticleConstants } from '@article/constants/article-constants';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleUtils } from '@article/util/article-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticlePlausiPropertyDef } from '@article/constants/article-plausi-property-def';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { Injectable } from '@angular/core';
import { UserModel } from '@user/model/user.model';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { CaeItem, CaeRail, createCaeItem } from '../../cae/cae-data.service';

@Injectable({ providedIn: 'root' })
export class CaeProjectHelper {

  public static readonly DEFAULT_ENDSTOP_ARTICLENO: string = '249-116';
  public static readonly DEFAULT_GROUP_MARKER_ARTICLENO: string = '2009-163';
  public static readonly DEFAULT_LABELSTRIP_ARTICLENO: string = '2009-110';

  constructor(
    private userModel: UserModel,
    private articleManager: ArticleManager,
  ) {}

  public prepareCaeProject(rail: CaeRail, mountingRailLeftSideOpen: boolean) {
    this.prepareRailAccessory(rail);
    for (const railItem of rail.items.concat()) {
      this.prepareRailItem(railItem, rail, mountingRailLeftSideOpen);
    }
  }

  private prepareRailAccessory(rail: CaeRail) {
    for (const accessory of rail.accessory) {
      const article = this.getArticleByCaeBaseArticle(accessory);

      if (article && ArticleUtils.isEndStopArticle(article)) {
        const bmk = rail.items[0].structure ? rail.items[0].structure : rail.structure;
        accessory.structure = bmk;
        this.moveSpaceToNewArticle(accessory, rail.items[0], article);
        rail.items.splice(0, 0, accessory);
        rail.accessory.splice(rail.accessory.indexOf(accessory), 1);
      }
    }
  }

  private prepareRailItem(item: CaeItem, rail: CaeRail, mountingRailLeftSideOpen: boolean) {
    const accessory: CaeItem[] = item.items;
    item.structure = item.structure ? item.structure : rail.structure;

    // erst endklammern abarbeiten
    const endStopArticles = accessory.filter((a) => {
      const article = this.getArticleByCaeBaseArticle(a);
      return !!article && ArticleUtils.isEndStopArticle(article);
    });
    for (const accessory of endStopArticles) {
      // create Item with data
      const endstopItem = accessory;
      endstopItem.structure = item.structure ? item.structure : rail.structure;
      // insert after article
      const insertPosition: number = rail.items.indexOf(item) + 1;
      rail.items.splice(insertPosition, 0, endstopItem);
      item.items.splice(item.items.indexOf(accessory), 1);
    }
    //  dann zwischenplatten, so dass die zwischenplatten dirket an den Artikel kommen
    const endPlateArticles = accessory.filter((a) => {
      const article = this.getArticleByCaeBaseArticle(a);
      return !!article && ArticleUtils.isEndPlateArticle(article);
    });
    for (const accessory of endPlateArticles) {
      const railArticle = this.getArticleByCaeBaseArticle(item);
      const insertLeft: boolean = this.validateEndplateLeftSide(item, railArticle, mountingRailLeftSideOpen);
      // create Item with data
      const endPlateItem = accessory;
      endPlateItem.rotation = item.rotation;
      endPlateItem.structure = item.structure ? item.structure : rail.structure;
      if (railArticle && insertLeft) {
        this.moveSpaceToNewArticle(endPlateItem, item, railArticle);
      }
      // insert after or before article
      const insertPosition: number = rail.items.indexOf(item) + (insertLeft ? 0 : 1);
      rail.items.splice(insertPosition, 0, endPlateItem);
      item.items.splice(item.items.indexOf(accessory), 1);
    }
  }

  private validateEndplateLeftSide(item: CaeItem, railArticle: ArticleVO | undefined, mountingRailLeftSideOpen: boolean): boolean {
    if (!railArticle) {
      return false;
    }
    const isLeftSideOpen: boolean = PropertyUtils.getBooleanPropertyValue(railArticle.plausiProperties, ArticlePlausiPropertyDef.IS_LEFT_SIDE_OPEN);
    let insertLeft = item.rotation === 180 || mountingRailLeftSideOpen;
    if (isLeftSideOpen) {
      insertLeft = !insertLeft;
    }
    return insertLeft;
  }

  public prepareBmkEndstops(rail: CaeRail): void {
    let bmkNames = this.findBmkNames(rail);

    if (!bmkNames || bmkNames.length === 0) {
      bmkNames = [rail.structure];
      rail.items.forEach((i) => (i.structure = rail.structure));
    }

    for (const name of bmkNames) {
      const hasTerminalBlocks = rail.items
        .filter((caeItem) => caeItem.structure === name)
        .some((caeItem) => {
          const article = this.getArticleByCaeBaseArticle(caeItem);
          const isCustomArticle = !article || ArticleUtils.isCustomArticle(article);
          const isTerminalBlockArticle = !!article && ArticleUtils.isTerminalBlockArticle(article);
          return isTerminalBlockArticle || isCustomArticle;
        });

      if (hasTerminalBlocks) {
        this.useEndstopIfExists(rail, name);
      }
    }
  }

  private useEndstopIfExists(rail: CaeRail, name: string): void {
    const firstItemIndex = rail.items.findIndex((item) => item.structure === name);
    const firstItem = firstItemIndex >= 0 ? rail.items[firstItemIndex] : undefined;
    if (firstItem) {
      const endStop: CaeItem = this.createEndstopWrapper(firstItem);
      if (endStop && endStop !== firstItem) {
        const createdArticle = this.getArticleByCaeBaseArticle(endStop);
        if (createdArticle) {
          endStop.structure = name;
          this.moveSpaceToNewArticle(endStop, firstItem, createdArticle);
          rail.items.splice(firstItemIndex, 0, endStop);
        }
      }
    }
  }

  private findBmkNames(rail: CaeRail): string[] {
    const names = rail.items
      .filter((item) => {
        const article: ArticleVO | undefined = this.getArticleByCaeBaseArticle(item);
        return !article || this.isBmkRelevantArticle(article)
      })
      .map((item) => item.structure)
      .filter((structure) => StringUtils.isNotEmpty(structure))
      .filter((structure, i, arr) => arr.findIndex((t) => t === structure) === i);

    return names;
  }

  private isBmkRelevantArticle(article: ArticleVO): boolean {
    return (
      !ArticleUtils.isIO_750_753Article(article) &&
      !ArticleUtils.isEndPlateArticle(article) &&
      !ArticleUtils.isEndStopArticle(article) &&
      !ArticleUtils.isCustomArticle(article)
    );
  }

  private createEndstopWrapper(endstop: CaeItem): CaeItem {
    const endstopArticle = this.getArticleByCaeBaseArticle(endstop);

    if (!!endstopArticle && !this.isTs35(endstopArticle)) {
      return endstop;
    }

    if (!endstopArticle || !ArticleUtils.isEndStopArticle(endstopArticle)) {
      endstop = createCaeItem({
        name: 'endstop_marker',
        articleNo: CaeProjectHelper.DEFAULT_ENDSTOP_ARTICLENO,
      });
    }

    let groupMarkerCarrier: CaeItem | undefined;
    if (endstop.items) {
      groupMarkerCarrier = endstop.items.find((item) => {
        const accessoryArticle: ArticleVO | undefined = this.getArticleByCaeBaseArticle(item);
        return !!accessoryArticle && ArticleUtils.isArticleOfTypeId(accessoryArticle, ArticleTypeIds.MARKER_CARRIER);
      });
    }

    if (!groupMarkerCarrier) {
      groupMarkerCarrier = createCaeItem({articleNo: CaeProjectHelper.DEFAULT_GROUP_MARKER_ARTICLENO});
      endstop.items = [groupMarkerCarrier];
    }

    return endstop;
  }

  private isTs35(article: ArticleVO): boolean {
    const givingConnections: ConnectionAreaVO[] = ArticleUtils.getConnectionElementsRec(article, ConnectionAreaVO.SIDE_GIVING);
    for (const givingConnection of givingConnections) {
      const typePropertyValue: string = PropertyUtils.getStringPropertyValue(givingConnection.connectionFeatures, ArticleConnectionPropertyDef.TYPE);
      if (typePropertyValue && typePropertyValue === ArticleConnectionPropertyValue.MOUNTING_RAIL_35_TYPE) {
        return true;
      }
    }
    return false;
  }

  private moveSpaceToNewArticle(newItem: CaeItem, nextItem: CaeItem, newArticle: ArticleVO) {
    if (!nextItem) {
        return;
    }
    if (nextItem.space > 0) {
        newItem.space = nextItem.space - LabelingUtils.getSpacingByArticle(newArticle, undefined, false);
        nextItem.space = 0;
    }

    if (nextItem.offset.x && nextItem.offset.x > 0) {
      newItem.offset.x = nextItem.offset.x - LabelingUtils.getSpacingByArticle(newArticle, undefined, false);
    }
}

  public getArticleByCaeBaseArticle(caeArticle: { type: string, articleNo: string }): ArticleVO | undefined {
    if (!caeArticle) {
      return undefined;
    }
    if (CaeProjectImportUtils.isCaeArticleCustomType(caeArticle)) {
      return this.articleManager.getCachedArticleByArticleNo(caeArticle.articleNo) ?? this.articleManager.getCachedArticleByArticleNo(ArticleConstants.CUSTOM_ARTICLE_NO);
    } else {
      const articleNo = this.getArticleNumberCleared(caeArticle.articleNo);
      if (!StringUtils.isEmpty(articleNo)) {
        return this.articleManager.getCachedArticleByArticleNo(articleNo!);
      }
    }
  }

  public getArticleNumberCleared(articleNo: string): string {
    if (StringUtils.isEmpty(articleNo)) {
      return articleNo;
    }

    const articleNumber = this.userModel.findArticleNumberByOwnArticleNumber(articleNo);
    if (StringUtils.isNotEmpty(articleNumber)) {
      return articleNumber;
    }
    return CaeProjectImportUtils.getArticleNumberWithoutWagoPrefix(articleNo);
  }
}
